// What cpq2 wrote on the order's lines LAST time, so an edit can change only that.
//
// Kevin, 2026-10-16: "sometimes i click edit config and then change one thing and submit.
// sometimes i have added information to the descriptions or i have changed the pricing and
// the cpq overwrites it for serial tag, ndb, or additional line items."
//
// The order's script merges each line's description three ways: what cpq2 generated before
// (from here), what it generates now, and what the line says today. Lines cpq2 owns follow the
// configuration; lines a person added or changed stay. Without the "before" it cannot tell the
// two apart, which is why every Edit Config used to replace the whole description.
//
// The "before" is the edit baseline: the item's saved configuration as this session loaded it
// (AnswersContext.getEditBaseline), run through the same builders as the new one.

import type { AnswerState, Question } from "../types";
import { buildSerialTagDescription } from "./serialTag";
import { buildAdditionalLines, resolveAdditionalLines } from "./additionalLines";

export type PriorLineDescriptions = {
  configured: string | null; // the item's Sales Description, DESCRIPTION/DESCRIPTION
  serialTag: string | null;
  additional: Record<string, string>; // item id -> description, for the add-ons that carry one
  // EVERY add-on the baseline brought, with or without a description, so the order can also
  // tell which lines the edit dropped and move quantities off them. Null when the lookup
  // failed: a partial list would read as "dropped" for everything missing from it.
  additionalLines: { itemId: string; itemName: string; description: string | null }[] | null;
};

/** The descriptions the baseline configuration produces. Null when there is no baseline for
 *  this item (a legacy item decoded from its mapping, or a configuration loaded from a
 *  different part): the order then has nothing to merge against. Never throws. */
export async function priorLineDescriptions(
  baseline: { itemId: string; answers: AnswerState } | null,
  itemId: string | number | null,
  questions: Question[],
  steps: { serialTag: boolean; additionalLines: boolean }
): Promise<PriorLineDescriptions | null> {
  if (!baseline || itemId == null || String(baseline.itemId) !== String(itemId)) return null;
  const a = baseline.answers;
  const out: PriorLineDescriptions = {
    configured: String(a["DESCRIPTION/DESCRIPTION"] ?? "").trim() || null,
    serialTag: null,
    additional: {},
    additionalLines: null,
  };
  try {
    if (steps.serialTag) out.serialTag = buildSerialTagDescription(a, questions);
  } catch {
    out.serialTag = null;
  }
  if (steps.additionalLines) {
    try {
      const lines = await resolveAdditionalLines(buildAdditionalLines(a, questions));
      out.additionalLines = lines.map((l) => ({ itemId: l.itemId, itemName: l.itemName, description: l.description ?? null }));
      for (const l of lines) {
        if (l.description) out.additional[l.itemId] = l.description;
      }
    } catch {
      /* no prior for the add-ons: the order replaces their descriptions as before */
    }
  }
  return out;
}
